/**
 * Types for the Export summary page.
 *
 * `ExportNode` / `ExportResponse` / `NodeTrackingLabel` come straight from the generated
 * client now that the export stack (SSTSW-1017/1018/1019/1020) has landed — re-exported
 * here so the rest of the feature imports its types from one place.
 */

import type { AntibodyResponse, NodeTrackingLabel, SequenceData } from '../../generated/generatedOpenAPI';

export type { ExportNode, ExportResponse, NodeTrackingLabel } from '../../generated/generatedOpenAPI';

/**
 * One rendered row of the export table — a node, flattened.
 *
 * The Figma table is flat (one row per node) while Hit selection is nested, so this
 * merges the clonotype-level fields from `AntibodyResponse` with the node-level fields
 * from its `SequenceData`, plus the export overlay from `ExportNode`.
 */
export interface ExportRow {
  /** Stable row key — node ids are only unique within an experiment. */
  nodeId: number;
  clonotypeId: number;
  nodeLabel: string;
  exportNums: number[];
  labels: NodeTrackingLabel[];

  /** Clonotype-level, from `AntibodyResponse`. */
  vjGeneHeavy: string;
  vjGeneLight: string;
  cdrh3: string;
  cdrl3: string;

  /** Node-level, from `SequenceData.metadata`. */
  nodeSize: number;
  aps: number | null;
  liabilityScore: number | null;
  shms: number | null;

  /** Handed straight to the shared cell renderers (ChainSequenceCell, LiabilityBadgeCell). */
  sequenceData: SequenceData;
  /** IMGT numbering for the chain columns, from the parent `AntibodyResponse`. */
  parent: AntibodyResponse;
}

/**
 * Maximum number of export tabs per experiment.
 *
 */
export const MAX_EXPORT_TABS = 10;
export const SELECTED_TAB_ID = 'selected' as const;
export type ExportTabId = string | typeof SELECTED_TAB_ID;
